import { computed, makeObservable } from 'mobx'
import type moment from 'moment'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'

interface CycleArgs {
  id: string
  begin: moment.Moment
  end: moment.Moment
  regularBegin: moment.Moment
  regularEnd: moment.Moment
}

export class Cycle {
  id: string
  begin: moment.Moment
  end: moment.Moment
  regularBegin: moment.Moment
  regularEnd: moment.Moment

  constructor({ id, begin, end, regularBegin, regularEnd }: CycleArgs) {
    makeObservable(this, {
      startEndSameYear: computed,
      yearLabel: computed,
      strictCycleCheck: computed,
    })
    this.id = id
    this.begin = begin.startOf('day')
    this.end = end.endOf('day')
    this.regularBegin = regularBegin.startOf('day')
    this.regularEnd = regularEnd.endOf('day')
  }

  covers(date: moment.Moment): boolean {
    return date.isSameOrAfter(this.begin) && date.isSameOrBefore(this.end)
  }

  regularRangeCovers(date: moment.Moment): boolean {
    return date.isSameOrAfter(this.regularBegin) && date.isSameOrBefore(this.regularEnd)
  }

  get strictCycleCheck(): boolean {
    return hasFeature('timeoff_strict_cycle_check_for_adjustments')
  }

  incidenceDateCheck(date: moment.Moment): boolean {
    return this.strictCycleCheck ? this.covers(date) : this.regularRangeCovers(date)
  }

  get startEndSameYear(): boolean {
    return this.begin.year() === this.end.year()
  }

  get yearLabel(): string {
    return this.startEndSameYear
      ? `${this.begin.year()}`
      : `${this.begin.year()}/${this.end.year()}`
  }

  get rangeLabel(): string {
    const cycleStartFormat = this.startEndSameYear ? 'ui_dm_long' : 'ui_dmy_long'
    const cycleEndFormat = 'ui_dmy_long'

    return i18n.t('time_off.cycle', {
      begin: i18nDateToString(this.begin, cycleStartFormat),
      end: i18nDateToString(this.end, cycleEndFormat),
    })
  }
}

interface IAllowanceStat {
  [key: string]: any
}

class AllowanceStat extends Model<IAllowanceStat> {
  constructor(attr: IAllowanceStat = {}) {
    super(attr)
    makeObservable(this, {
      cycles: computed.struct,
      firstCycle: computed,
      lastCycle: computed,
    })
  }

  static urlRoot() {
    return '/allowance_stats'
  }

  get cycles(): Cycle[] {
    let cycles = this.attributes.cycles

    if (!Array.isArray(cycles)) cycles = []

    return cycles.map(
      (cycle: any) =>
        new Cycle({
          id: cycle.id,
          begin: stringToDate(cycle.start_at, 'backend'),
          end: stringToDate(cycle.end_at, 'backend'),
          regularBegin: stringToDate(cycle.regular_start_at, 'backend'),
          regularEnd: stringToDate(cycle.regular_end_at, 'backend'),
        })
    )
  }

  get firstCycle() {
    return this.cycles[0]
  }

  get lastCycle() {
    return this.cycles[this.cycles.length - 1]
  }

  cycleIndex(cycle: Cycle): number {
    return this.cycles.findIndex((item) => item === cycle)
  }

  prevCycle(cycle: Cycle): Cycle | null | undefined {
    return this.cycles[this.cycleIndex(cycle) - 1]
  }

  nextCycle(cycle: Cycle): Cycle | null | undefined {
    return this.cycles[this.cycleIndex(cycle) + 1]
  }

  cycleByDate(date: moment.Moment): Cycle | null | undefined {
    return this.cycles.find((cycle) => cycle.covers(date))
  }
}

export default AllowanceStat
